import { ArrowUpRight } from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { avatarInitials, avatarSlotFor } from '../../../lib/avatar-color';
import type { CanvasPlayer } from '../../../lib/home3-player';
import type { Home3Event } from '../../../lib/home3-feed';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { panelHeadline, panelRowLinkKey, panelRowText, panelRowWho } from '../../../lib/home3-panel-row';
import { outreachSiblings } from '../../../lib/outreach-thread-stepper';
import { AgentDot } from '../../AgentDot';
import { MaestroMark } from '../MaestroMark';
import { Avatar, TextButton, Typography } from '../ui';
import type { Home3Messenger } from './Home3Categories';
import { ACTIVITY_MAP_BI_PAGE } from '../../../lib/activity-map-bi';
import { useHome3EventOpener } from './use-home3-event-opener';
import styles from './Home3ActivityPanel.module.css';

export { panelRowText };

/** The most rows the panel lays out — enough to fill it; older ones have risen away under the top blur. */
const MAX_ROWS = 14;
/** The prototype's typing: the dots for this long, then the text at this many characters a second. */
const TYPE_DOTS_MS = 900;
const TYPE_CPS = 38;

/** How long a row takes to type in, dots included — the player leaves each row this long before the next. */
export function panelTypingMs(text: string): number {
  return TYPE_DOTS_MS + (text.length / TYPE_CPS) * 1000;
}

/**
 * The canvas's activity panel (Figma 2038:96776): what the reader has not seen yet, REPLAYED with the player.
 * Web-only, as the whole canvas is — the tracked divergence in `.claude/rules/orion-web-home.md`.
 *
 * The rows are the canvas's unseen items (`canvasUnseen`, oldest first) — a row is on the panel once the
 * playhead has reached it, newest at the foot, as many as fit, the oldest fading under the top edge. As the
 * playhead reaches one it arrives as the "Symphony Live" prototype's do: the list slides up, the row fades
 * in, three dots bounce, then its text types itself in and its link appears. The player leaves each row the
 * time it takes to type (`panelTypingMs`), so the next row comes only once this one has typed in; `shownRef`
 * tells it how far the typing has got, should it fall behind. A seek (or the loop going round)
 * lays the list out at once, with nothing typing. Paused, everything holds still.
 *
 * Each row opens what the board's row opens (`useHome3EventOpener`). The headline counts the rows reached in
 * this loop, so it rises by one as each row arrives, as the prototype's does, holds while paused, follows a
 * seek, and goes back to zero when the loop goes round. At a loop's end it is everything not yet seen.
 */
export function Home3ActivityPanel({
  events,
  player,
  shownRef,
  agentNames,
  spaceNames,
  maestroName,
  senders,
}: {
  /** The unseen items, oldest first — the player's track holds the same ones. */
  events: Home3Event[];
  player: CanvasPlayer | null;
  /** How many of the reached rows have finished typing in — what the player waits on should typing fall behind. */
  shownRef?: { current: number };
  agentNames: Record<string, string>;
  spaceNames: Record<string, string>;
  maestroName: string;
  /** A message row's face, by outreach id: the person the thread is with, from the contact book. */
  senders?: Record<string, Home3Messenger>;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const opener = useHome3EventOpener({ agentNames, spaceNames });
  const listRef = useRef<HTMLDivElement | null>(null);
  const playing = !!player?.playing;

  const reached = player ? Math.min(player.reached, events.length) : 0;
  const rows = useMemo(() => events.slice(0, reached).slice(-MAX_ROWS), [events, reached]);
  const updates = player ? reached : null;
  // ONE sentence for the translator ("{updates} since your last visit"), with the counted phrase spliced in
  // where their language puts it — Japanese and Korean put the visit first — so it can be set in bold. At zero it
  // is its own sentence with no count ("No new updates since your last visit", `panelHeadline`) — but only when the
  // feed is truly empty: at the start of a replay of N > 0 it is the counted "0 new updates", with N about to play.
  const headline = panelHeadline(updates, t, player ? player.total : null);

  // ## Arrivals: which rows came in by PLAYING on (they type in), and which by a jump (they just are)
  const known = useRef<Set<string> | null>(null);
  const seenJumps = useRef(-1);
  const typing = useRef(new Map<string, number>());
  const [, setTick] = useState(0);
  const arrived = useRef<string[]>([]);
  const jumped = player ? player.jumps !== seenJumps.current : false;
  const ids = rows.map((r) => r.id);
  if (player) {
    if (jumped || known.current === null) {
      known.current = new Set(ids);
      seenJumps.current = player.jumps;
      typing.current.clear();
      arrived.current = [];
    } else {
      const fresh = ids.filter((id) => !known.current!.has(id));
      if (fresh.length) {
        for (const id of fresh) {
          known.current.add(id);
          typing.current.set(id, 0);
        }
        arrived.current = fresh;
      }
    }
  }

  // What the playhead waits on: the rows it has reached, less the ones still typing in.
  const reachedRef = useRef(0);
  reachedRef.current = reached;
  if (shownRef) shownRef.current = reached - typing.current.size;

  // The list slides up by what arrived, and each arrival fades in — measured after the arrival is laid out.
  useLayoutEffect(() => {
    const list = listRef.current;
    const fresh = arrived.current;
    if (!list || !fresh.length) return;
    arrived.current = [];
    const nodes = fresh.map((id) => list.querySelector<HTMLElement>(`[data-row="${CSS.escape(id)}"]`)).filter(Boolean) as HTMLElement[];
    const gap = parseFloat(getComputedStyle(list).rowGap) || 0;
    const h = nodes.reduce((sum, n) => sum + n.offsetHeight + gap, 0);
    list.animate([{ transform: `translateY(${h}px)` }, { transform: 'translateY(0)' }], { duration: 560, easing: 'cubic-bezier(.2,.8,.2,1)' });
    for (const n of nodes) n.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 480, delay: 120, fill: 'backwards' });
  });

  // The typing clock: it only runs while the player plays, so a pause holds a half-typed row where it is.
  useEffect(() => {
    if (!playing) return undefined;
    let raf = 0;
    let last = performance.now();
    let lastPaint = 0;
    const tick = (now: number) => {
      const dt = now - last;
      last = now;
      let active = false;
      for (const [id, ms] of typing.current) {
        const event = events.find((e) => e.id === id);
        const total = event ? panelTypingMs(textOf(event)) : 0;
        if (ms >= total) {
          typing.current.delete(id);
          if (shownRef) shownRef.current = reachedRef.current - typing.current.size;
        }
        else {
          typing.current.set(id, ms + dt);
          active = true;
        }
      }
      if (active && now - lastPaint > 45) {
        lastPaint = now;
        setTick((n) => n + 1);
      } else if (!active && lastPaint) {
        lastPaint = 0;
        setTick((n) => n + 1);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  });

  // The slide and the fades pause with the player too.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    for (const a of list.getAnimations({ subtree: true })) {
      if (playing) a.play();
      else a.pause();
    }
  }, [playing]);

  const textOf = (event: Home3Event): string => panelRowText(event, t);
  const siblings = outreachSiblings(rows, opener.threadSibling);
  const open = (event: Home3Event) => {
    // The panel is the canvas's alone, so its row reports the Activity Map's name, with where the row goes.
    logPageClickBi(
      { page_name: ACTIVITY_MAP_BI_PAGE, button_name: 'activity-map-panel:open', answer: event.destination },
      { additionalFields: { destination: event.destination } },
    );
    opener.open(event, siblings);
  };

  return (
    // `data-canvas-panel`: the map centres the octopus in the canvas beside it (`HomeMap`).
    <aside className={`${styles.panel}${playing ? '' : ` ${styles.paused}`}`} aria-label={t('home.overview.recentActivity')} data-canvas-panel>
      {headline && (
        // Figma's Archivo 30/32 — the count at the SemiBold tier, the rest at the Medium one.
        <Typography variant="h1" color="primary" component="h2" className={styles.headline}>
          {headline.kind === 'none' ? (
            headline.text
          ) : (
            <>
              {headline.before}
              <span className={styles.count}>{headline.count}</span>
              {headline.after}
            </>
          )}
        </Typography>
      )}
      <div className={styles.viewport}>
        <div ref={listRef} className={styles.list}>
          {rows.map((event) => {
            const ms = typing.current.get(event.id);
            const text = textOf(event);
            const typed = ms === undefined ? text.length : ms < TYPE_DOTS_MS ? 0 : Math.floor(((ms - TYPE_DOTS_MS) / 1000) * TYPE_CPS);
            return (
              <Home3ActivityRow
                key={event.id}
                event={event}
                text={text}
                typed={typed}
                typing={ms !== undefined && ms < TYPE_DOTS_MS}
                done={typed >= text.length}
                agentNames={agentNames}
                maestroName={maestroName}
                senders={senders}
                onOpen={() => open(event)}
              />
            );
          })}
        </div>
        {/* The prototype's top band: what rises past the list's 70% blurs and fades into the page. */}
        <div className={styles.blur} aria-hidden />
        {/* …and the bottom fade a new row rises out of. */}
        <div className={styles.fade} aria-hidden />
      </div>
      {opener.modals}
    </aside>
  );
}

/** A message row's face: the person the thread is with, whoever wrote last. The book's card where it has one (its
 *  photo, its colour seed, as the Outreach tile draws them); else the row's own name — a received row carries it on
 *  its second line, a sent row (and one with nothing said) as its title. */
function senderOf(event: Home3Event, senders: Record<string, Home3Messenger> | undefined): Home3Messenger {
  const card = senders?.[event.id.replace(/^(received|sent):/, '')];
  const sub = event.subtitle;
  const name = card?.name || (sub.kind === 'contact' && sub.name) || (event.titleKey ? '' : event.title);
  return { name, photoUrl: card?.photoUrl, seed: card?.seed || name || event.id };
}

/**
 * ONE activity row (Figma 2038:96783): the face, the time and who, the text, the link. The desktop panel types its
 * text in (`text` grows, `typing` shows the dots, the link waits for `done`); the phones' Recent activity sheet on
 * mobile web (`Home3PhoneLiveSheet`) passes it whole. One component, so the two cannot draw a row differently.
 */
export function Home3ActivityRow({
  event,
  text,
  typed,
  typing = false,
  done = true,
  agentNames,
  maestroName,
  senders,
  onOpen,
}: {
  event: Home3Event;
  /** What the row says — all of `panelRowText`. It is laid out WHOLE from the first frame, the part not yet typed (`typed`
   *  onward) drawn clear, so the row never grows as it types: its line wraps, and so its height, are fixed. */
  text: string;
  /** How many characters have typed in; absent, all of them. */
  typed?: number;
  /** The typing dots, before the first character. */
  typing?: boolean;
  /** The text is all in: the link shows. */
  done?: boolean;
  agentNames: Record<string, string>;
  maestroName: string;
  senders?: Record<string, Home3Messenger>;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const sub = event.subtitle;
  const sender = sub.kind === 'contact' ? senderOf(event, senders) : null;
  return (
    <div data-row={event.id} className={styles.row}>
      <span className={styles.avatar}>
        {sub.kind === 'agent' ? (
          <AgentDot id={sub.agentId} size={20} />
        ) : sender ? (
          <Avatar initials={avatarInitials(sender.name)} photoUrl={sender.photoUrl} size={20} color={avatarSlotFor(sender.seed)} />
        ) : (
          // Maestro's own rows, and a task nobody has been put on yet: the Maestro is the one holding it.
          <MaestroMark size={20} />
        )}
      </span>
      <span className={styles.body}>
        <span className={styles.meta}>
          <Typography variant="p2" color="secondary" component="span" className={styles.time}>
            {new Date(event.at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
          </Typography>
          <Typography variant="h4" color="primary" component="span" noWrap>
            {panelRowWho(event, agentNames, maestroName, t)}
          </Typography>
        </span>
        <Typography variant="heading-04" color="primary" component="span" className={styles.text}>
          {typed === undefined ? text : text.slice(0, typed)}
          {typed !== undefined && typed < text.length && (
            <span className={styles.untyped} aria-hidden>
              {text.slice(typed)}
            </span>
          )}
          {/* Over the start of the line, never beside it: the dots take no room of their own. */}
          {typing && (
            <span className={styles.dots} aria-hidden>
              <span />
              <span />
              <span />
            </span>
          )}
        </Typography>
        {/* Always laid out, so the row keeps its height; it fades in once the text is in. */}
        <span className={`${styles.link}${done ? '' : ` ${styles.linkWaiting}`}`} aria-hidden={!done}>
          <TextButton
            size="small"
            underline
            endIcon={<ArrowUpRight size={16} aria-hidden className="dir-flip" />}
            onClick={onOpen}
            tabIndex={done ? undefined : -1}
          >
            {t(panelRowLinkKey(event))}
          </TextButton>
        </span>
      </span>
    </div>
  );
}
